import * as Sentry from '@sentry/react';

import { ANALYTIC_EVENTS, Analytics } from 'analytics/Analytics';
import { SEARCH_PARAMS } from 'constants/search-params';
import { startOverlayLoader } from 'store/app/slice';
import type { PaymentType } from 'types';
import { getQueryParams } from 'utils';

import { ErrorStatuses, PaymentStatus } from '../constants/payment';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useSendPayMutation } from '../store/payment/service';
import { setAcquiring, setPaymentData } from '../store/payment/slice';
import { createAndSubmit3DSSecureForm } from '../utils/create-and-submit-3DS';

import { useLoadData } from './useLoadData';
import { useMainNavigate } from './useMainNavigate';
import { useTranslate } from './useTranslate';
import { useYM } from './useYm';

const usePayment = () => {
  const { isInvoice, isPayment } = useAppSelector((state) => state.payment);
  const [sendPay, { isLoading }] = useSendPayMutation();
  const { loadData } = useLoadData();
  const { trackOnClick } = useYM();
  const { t } = useTranslate();
  const { goToPaymentView } = useMainNavigate();
  const dispatch = useAppDispatch();

  // Только запрос в процессинг, без навигации. Нужен Apple Pay:
  // пока открыт платёжный шит, уход со страницы уничтожает сессию
  const sendPayment = async (payData: any) => {
    trackOnClick(isPayment ? t('lang.pay') : t('lang.payout'));
    const res = await sendPay({
      values: payData,
      url: isInvoice ? 'invoice' : 'payment',
    }).unwrap();

    if (ErrorStatuses.includes(res.status)) {
      Sentry.captureMessage(`${res.error}
        ${JSON.stringify(res)}
      `);
    }

    return res;
  };

  // Обработка ответа процессинга: 3DS, редирект, перезагрузка данных
  const handlePaymentResult = (res: PaymentType) => {
    if (
      res.status === PaymentStatus.WAIT_PROCESSING ||
      res.status === PaymentStatus.WAIT_PAYMENT_CONFIRM
    ) {
      dispatch(startOverlayLoader());
    }

    if (res.status === PaymentStatus.WAIT_PAYMENT_CONFIRM && res.acsData?.acsUrl) {
      createAndSubmit3DSSecureForm(res.acsData);
    } else if (
      res.status === PaymentStatus.WAIT_PROCESSING &&
      res.redirectUser &&
      res.redirectUrl
    ) {
      Analytics.sentEvent(ANALYTIC_EVENTS.redirect);
      dispatch(startOverlayLoader());
      window.location.href = res.redirectUrl;
    } else if (
      res.status !== PaymentStatus.INTERNAL_ERROR &&
      res.status !== PaymentStatus.INCORRECT_DATA
    ) {
      Analytics.sentEvent(ANALYTIC_EVENTS.processingSuccess);

      if (isInvoice) {
        goToPaymentView(res.ID);
      } else {
        loadData();
      }
    } else {
      Analytics.sentEvent(ANALYTIC_EVENTS.processingError, {
        transaction_id: getQueryParams(SEARCH_PARAMS.ID),
        message: JSON.stringify(res),
      });

      dispatch(setPaymentData(res));
    }
  };

  const fetchSendPayment = async (payData: any) => {
    const res = await sendPayment(payData);

    handlePaymentResult(res);

    return res;
  };

  const closePayMethod = () => {
    dispatch(setAcquiring(null));
  };

  return {
    isLoading,
    fetchSendPayment,
    sendPayment,
    handlePaymentResult,
    closePayMethod,
  };
};

export { usePayment };
